// The consult's microphone noise gate, ported from ottos-consult
// src/consult/micGate.js (see PORTED-FROM.md), now on 20 ms frames at 16 kHz.
//
// Why it is soft (the Sept 14 2026 lesson, kept): the earlier gate (0.01, 400 ms
// hangover, frames under the gate zeroed) cut the soft start of an answer, so
// Gemini heard fragments such as "cars ourselves". The gate is low, holds
// 600 ms, keeps a short pre-roll so the first syllable goes out at full level,
// and turns quiet frames DOWN (attenuate) instead of erasing them.
//
// Values come from @otto/config MicSettings (the "home" profile), never from
// here: this file holds the logic only.
import { MicSettings, type MicGateMode } from "@otto/config";
import { float32ToPcm16 } from "./pcm";

export type GateFrameKind = "sent" | "preroll" | "attenuated" | "silenced";

export interface GateOutput {
  /** This frame itself clears the threshold. */
  above: boolean;
  /** Send this frame at full level (above, or inside the hangover). */
  open: boolean;
  /** PCM16 buffers to send, in order. */
  frames: { kind: GateFrameKind; pcm: ArrayBuffer }[];
  /** Frames not sent at all (drop mode). */
  dropped: number;
}

export interface MicGateOptions {
  threshold: number;
  hangoverMs: number;
  mode: MicGateMode;
  floorGain: number;
  preRollMs: number;
  /** Echo guard margin (echoGuard.ts); 0 is off. */
  echoMargin: number;
  /** While Otto plays, how long the owner must stay above the guard before the gate opens. */
  echoConfirmMs: number;
}

export function gateOptionsFrom(settings: MicSettings): MicGateOptions {
  return {
    threshold: settings.gate_threshold_rms,
    hangoverMs: settings.gate_hangover_ms,
    mode: settings.gate_mode,
    floorGain: settings.gate_floor_gain,
    preRollMs: settings.gate_preroll_ms,
    echoMargin: settings.echo_guard_margin,
    echoConfirmMs: settings.echo_guard_confirm_ms,
  };
}

/** The defaults Wes approved on Sept 14 2026, read from @otto/config. */
export function defaultGateOptions(): MicGateOptions {
  return gateOptionsFrom(MicSettings.parse({}));
}

/** How many whole frames cover the pre-roll. Any positive pre-roll keeps at least one frame. */
export function preRollFrameCount(preRollMs: number, frameMs: number): number {
  if (!(preRollMs > 0) || !(frameMs > 0)) return 0;
  return Math.max(1, Math.round(preRollMs / frameMs));
}

function attenuated(samples: Float32Array, gain: number): ArrayBuffer {
  const level = Number.isFinite(gain) ? Math.min(1, Math.max(0, gain)) : 0.25;
  const scaled = new Float32Array(samples.length);
  for (let index = 0; index < samples.length; index += 1) scaled[index] = (samples[index] ?? 0) * level;
  return float32ToPcm16(scaled);
}

const silentFrames = new Map<number, ArrayBuffer>();

/** A zero PCM16 frame of `sampleCount` samples (cached; never mutate it). */
export function silentPcm16(sampleCount: number): ArrayBuffer {
  const count = Math.max(0, Math.floor(sampleCount));
  let frame = silentFrames.get(count);
  if (!frame) {
    frame = new Int16Array(count).buffer;
    silentFrames.set(count, frame);
  }
  return frame;
}

/**
 * Routes each 16 kHz frame through the gate. Pre-roll holds the most recent
 * frames under the gate back; when the gate opens they go out at full level
 * ahead of the frame that opened it, so nothing is sent twice and the timeline
 * the voice engine sees stays continuous (trailing silence reaches it one
 * pre-roll later: 100 ms, about 5 frames).
 *
 * `now` is in milliseconds. The call passes frame time (frame count x 20 ms),
 * so the gate runs on the audio clock, not on a busy main thread's clock.
 */
export function createMicGateRouter(options: MicGateOptions) {
  const { threshold, hangoverMs, mode, floorGain, preRollMs, echoConfirmMs } = options;
  let lastAboveAt = Number.NEGATIVE_INFINITY;
  let held: Float32Array[] = [];
  let loudRunMs = 0;

  const closedFrame = (samples: Float32Array, echo: boolean): { kind: GateFrameKind; pcm: ArrayBuffer } | null => {
    // While Otto plays, a closed frame is silence, not turned down: a quiet copy of his own voice is still his voice.
    if (echo && mode !== "drop") return { kind: "silenced", pcm: silentPcm16(samples.length) };
    if (mode === "attenuate") return { kind: "attenuated", pcm: attenuated(samples, floorGain) };
    if (mode === "silence") return { kind: "silenced", pcm: silentPcm16(samples.length) };
    return null;
  };

  return {
    mode,
    get heldFrames() {
      return held.length;
    },
    /** `echoThreshold` (echoGuard.ts) raises the gate while Otto plays; 0 when he is quiet. */
    process(samples: Float32Array, frameRms: number, now: number, frameMs: number, echoThreshold = 0): GateOutput {
      const echo = echoThreshold > threshold;
      const level = echo ? echoThreshold : threshold;
      // A threshold of 0 or less means the gate is off: every frame passes.
      const loud = !(level > 0) || frameRms >= level;
      loudRunMs = loud ? loudRunMs + frameMs : 0;
      // Over Otto's voice, a sound must last (echoConfirmMs) to open the gate; the pre-roll keeps its start.
      const above = loud && (!echo || loudRunMs >= echoConfirmMs);
      if (above) lastAboveAt = now;
      // Over Otto's voice the hangover does not hold the gate open (Oct 2 2026): his own first syllable, let through,
      // kept it open 600 ms, long enough for the voice engine to take it for the owner. Only sound above his echo passes.
      const open = above || (!echo && now - lastAboveAt < hangoverMs);
      const frames: GateOutput["frames"] = [];
      let dropped = 0;
      if (open) {
        for (const frame of held) frames.push({ kind: "preroll", pcm: float32ToPcm16(frame) });
        held = [];
        frames.push({ kind: "sent", pcm: float32ToPcm16(samples) });
        return { above, open, frames, dropped };
      }
      held.push(samples);
      const limit = preRollFrameCount(preRollMs, frameMs);
      while (held.length > limit) {
        const frame = closedFrame(held.shift() as Float32Array, echo);
        if (frame) frames.push(frame);
        else dropped += 1;
      }
      return { above, open, frames, dropped };
    },
    /** Forget the hangover and throw away held audio, for example on mute. */
    reset() {
      lastAboveAt = Number.NEGATIVE_INFINITY;
      held = [];
      loudRunMs = 0;
    },
  };
}

export type MicGateRouter = ReturnType<typeof createMicGateRouter>;

const MODES: readonly MicGateMode[] = ["attenuate", "silence", "drop"];

/**
 * Lab overrides from the URL, kept from today's consult so a call can be tuned
 * without a deploy: ?micGate=0.02&micGateMode=silence&micGateHangover=800
 * &micGateFloor=0.2&micGatePreRoll=120. Anything that does not parse is ignored.
 */
export function gateOptionsWithQuery(base: MicGateOptions, search: string): MicGateOptions {
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(search);
  } catch {
    return base;
  }
  const number = (key: string, fallback: number, max = Number.POSITIVE_INFINITY) => {
    const raw = params.get(key);
    if (raw === null || raw.trim() === "") return fallback;
    const value = Number(raw);
    return Number.isFinite(value) && value >= 0 && value <= max ? value : fallback;
  };
  const modeRaw = params.get("micGateMode")?.trim().toLowerCase() as MicGateMode | undefined;
  return {
    threshold: number("micGate", base.threshold, 1),
    hangoverMs: number("micGateHangover", base.hangoverMs, 5000),
    mode: modeRaw && MODES.includes(modeRaw) ? modeRaw : base.mode,
    floorGain: number("micGateFloor", base.floorGain, 1),
    preRollMs: number("micGatePreRoll", base.preRollMs, 1000),
    echoMargin: number("micEcho", base.echoMargin, 10),
    echoConfirmMs: number("micEchoConfirm", base.echoConfirmMs, 500),
  };
}
